// Generic config-driven register for Phase 8 People & Training modules.
// Handles list + filters + create/edit dialog + single file (certificate/evidence) upload/download.
import { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, Search, Pencil, Trash2, X, Save, Loader2, Paperclip, Upload, FileDown, GraduationCap } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useView } from "@/context/ViewContext";
import { useSortableRows, SortableTh } from "@/lib/sortable";

const INTERNAL = new Set(["director", "administrator", "consultant"]);

const STATUS_TONE = {
    Valid: { bg: "#E8F1E6", fg: "#3F7D3A" }, Active: { bg: "#E8F1E6", fg: "#3F7D3A" },
    "Expiring soon": { bg: "#FCF1DC", fg: "#C68A1C" },
    Expired: { bg: "#FBE8E5", fg: "#B33A2E" },
    Booked: { bg: "#E7EEF9", fg: "#2A5DB0" }, Pending: { bg: "#E7EEF9", fg: "#2A5DB0" },
    "Not started": { bg: "#F0EDE8", fg: "#6F6A64" },
};

function Pill({ value }) {
    const t = STATUS_TONE[value] || STATUS_TONE["Not started"];
    return <span className="inline-block px-2.5 py-0.5 rounded-full text-[11px] font-semibold" style={{ background: t.bg, color: t.fg }}>{value}</span>;
}

export default function PeopleRegister({
    testid, title, subtitle, endpoint, fields, columns, fileLabel = "Certificate",
    statusFilterKey = "statuses", icon: Icon = GraduationCap,
    scoped = true, editRoles, showClientField = true,
}) {
    const { user } = useAuth();
    const { isInternalView } = useView();
    const canEdit = user && (editRoles ? editRoles.has(user.role) : new Set(["director", "administrator", "client_admin"]).has(user.role));
    const isInternalUser = user && INTERNAL.has(user.role);

    const [rows, setRows] = useState([]);
    const [meta, setMeta] = useState({});
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [clientFilter, setClientFilter] = useState("");
    const [dialog, setDialog] = useState(null); // {mode, record}

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [list, m] = await Promise.all([api.get(endpoint), api.get(`${endpoint}/meta`)]);
            setRows(list.data);
            setMeta(m.data);
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to load"); }
        finally { setLoading(false); }
    }, [endpoint]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { if (scoped && showClientField) api.get("/clients").then((r) => setClients(r.data)).catch(() => {}); }, [scoped, showClientField]);

    const clientName = (id) => clients.find((c) => c.id === id)?.name || (id ? "—" : "Priority Risk");

    async function removeRow(r) {
        if (!window.confirm("Delete this record?")) return;
        try { await api.delete(`${endpoint}/${r.id}`); load(); toast.success("Deleted"); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    }

    async function viewFile(r) {
        try {
            const resp = await api.get(`${endpoint}/${r.id}/file`, { responseType: "blob" });
            window.open(URL.createObjectURL(resp.data), "_blank");
        } catch (e) { toast.error("Could not open file"); }
    }

    const statusOptions = meta[statusFilterKey] || [];
    const filtered = rows.filter((r) => {
        if (statusFilter && r.computed_status !== statusFilter) return false;
        if (clientFilter && (r.client_id || "") !== clientFilter) return false;
        if (!q) return true;
        const hay = columns.map((c) => (c.search ? c.search(r) : (r[c.key] ?? ""))).join(" ").toLowerCase();
        return hay.includes(q.toLowerCase());
    });

    const accessors = useMemo(() => {
        const acc = { __status: (r) => r.computed_status || r.status || "" };
        columns.forEach((c) => { acc[c.key] = c.sortValue ? (r) => c.sortValue(r) : (c.search ? (r) => c.search(r) : (r) => r[c.key]); });
        return acc;
    }, [columns]);
    const { sortKey, sortDir, toggleSort, sorted } = useSortableRows(filtered, { accessors });

    return (
        <div data-testid={`${testid}-page`} className="space-y-5">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <span className="inline-block pb-1 text-[10.5px] font-bold tracking-[.08em] uppercase" style={{ borderBottom: "2px solid var(--brand-accent,#C75A1B)", color: "#6F6A64" }}>People &amp; Training</span>
                    <h2 className="text-2xl font-bold tracking-tight mt-2 text-[#1F1F1F] flex items-center gap-2"><Icon className="w-5 h-5" style={{ color: "var(--brand-accent,#C75A1B)" }} /> {title}</h2>
                    <p className="text-[13px] mt-1" style={{ color: "#6F6A64" }}>{rows.length} records{subtitle ? ` · ${subtitle}` : ""}</p>
                </div>
                {canEdit && (
                    <button data-testid={`${testid}-new-button`} onClick={() => setDialog({ mode: "create", record: {} })}
                        className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-[8px] text-[12.5px] font-semibold text-white" style={{ background: "var(--brand-accent,#C75A1B)" }}><Plus className="w-3.5 h-3.5" /> New record</button>
                )}
            </div>

            <div className="flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-2 bg-white border rounded-[8px] px-3 py-1.5 flex-1 min-w-[200px] max-w-[320px]" style={{ borderColor: "#E5E0DA" }}>
                    <Search className="w-3.5 h-3.5" style={{ color: "#6F6A64" }} />
                    <input data-testid={`${testid}-search`} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="flex-1 bg-transparent outline-none text-[12.5px]" />
                </div>
                <select data-testid={`${testid}-status-filter`} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="h-9 px-3 rounded-lg border bg-white text-sm" style={{ borderColor: "#E5E0DA" }}>
                    <option value="">All statuses</option>
                    {statusOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                {isInternalUser && scoped && showClientField && (
                    <select data-testid={`${testid}-client-filter`} value={clientFilter} onChange={(e) => setClientFilter(e.target.value)} className="h-9 px-3 rounded-lg border bg-white text-sm" style={{ borderColor: "#E5E0DA" }}>
                        <option value="">All clients</option>
                        {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                )}
            </div>

            <div className="bg-white border rounded-[12px] overflow-hidden" style={{ borderColor: "#E5E0DA" }}>
                <table className="w-full text-[12.5px]">
                    <thead className="bg-[#F6F4F0] text-[#6F6A64]">
                        <tr>
                            {columns.map((c) => (
                                <SortableTh key={c.key} myKey={c.key} sortKey={sortKey} sortDir={sortDir} onClick={toggleSort}
                                    testId={`${testid}-th-${c.key}`} className="group py-2.5">{c.label}</SortableTh>
                            ))}
                            <SortableTh myKey="__status" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort}
                                testId={`${testid}-th-status`} className="group py-2.5">Status</SortableTh>
                            <th className="px-3 py-2.5"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && <tr><td colSpan={columns.length + 2} className="px-3 py-8 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" style={{ color: "#C75A1B" }} /></td></tr>}
                        {!loading && sorted.length === 0 && <tr><td colSpan={columns.length + 2} className="px-3 py-8 text-center italic" style={{ color: "#9A968F" }}>No records.</td></tr>}
                        {!loading && sorted.map((r) => (
                            <tr key={r.id} data-testid={`${testid}-row-${r.id}`} className="border-t hover:bg-[#FBFAF8]" style={{ borderColor: "#F0EDE8" }}>
                                {columns.map((c) => <td key={c.key} className="px-3 py-2.5 align-top text-[#2D2D2D]">{c.render ? c.render(r, { clientName }) : (r[c.key] || "—")}</td>)}
                                <td className="px-3 py-2.5 align-top"><Pill value={r.computed_status || r.status} /></td>
                                <td className="px-3 py-2.5 text-right whitespace-nowrap">
                                    {r.attachment && (
                                        <button data-testid={`${testid}-file-${r.id}`} onClick={() => viewFile(r)} className="w-7 h-7 rounded-[6px] grid place-items-center hover:bg-[#EEF3FB] inline-grid align-middle" title={`View ${fileLabel.toLowerCase()}`}><FileDown className="w-3.5 h-3.5" style={{ color: "#2A5DB0" }} /></button>
                                    )}
                                    {canEdit && (
                                        <>
                                            <button data-testid={`${testid}-edit-${r.id}`} onClick={() => setDialog({ mode: "edit", record: r })} className="w-7 h-7 rounded-[6px] grid place-items-center hover:bg-[#F6F4F0] inline-grid align-middle" title="Edit"><Pencil className="w-3.5 h-3.5" style={{ color: "#6F6A64" }} /></button>
                                            <button data-testid={`${testid}-delete-${r.id}`} onClick={() => removeRow(r)} className="w-7 h-7 rounded-[6px] grid place-items-center hover:bg-[#FBE8E5] inline-grid align-middle" title="Delete"><Trash2 className="w-3.5 h-3.5" style={{ color: "#B33A2E" }} /></button>
                                        </>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {dialog && (
                <RecordDialog testid={testid} endpoint={endpoint} mode={dialog.mode} record={dialog.record}
                    fields={fields} meta={meta} fileLabel={fileLabel}
                    clients={clients} showClientField={scoped && showClientField && isInternalUser}
                    onClose={() => setDialog(null)} onSaved={() => { setDialog(null); load(); }} />
            )}
        </div>
    );
}

function RecordDialog({ testid, endpoint, mode, record, fields, meta, fileLabel, clients, showClientField, onClose, onSaved }) {
    const [form, setForm] = useState({});
    const [file, setFile] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        const init = {};
        fields.forEach((f) => { init[f.key] = record?.[f.key] ?? (f.type === "select" ? (meta[f.optionsKey]?.[0] || "") : ""); });
        if (showClientField) init.client_id = record?.client_id || "";
        setForm(init);
    }, [record]); // eslint-disable-line

    const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

    async function save() {
        for (const f of fields) if (f.required && !form[f.key]?.trim?.()) { toast.error(`${f.label} is required`); return; }
        setBusy(true);
        const payload = {};
        fields.forEach((f) => { payload[f.key] = form[f.key] || null; });
        if (showClientField) payload.client_id = form.client_id || null;
        try {
            let id = record?.id;
            if (mode === "create") { const { data } = await api.post(endpoint, payload); id = data.id; }
            else await api.patch(`${endpoint}/${id}`, payload);
            if (file && id) {
                const fd = new FormData();
                fd.append("file", file);
                await api.post(`${endpoint}/${id}/file`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            }
            toast.success(mode === "create" ? "Record created" : "Record updated");
            onSaved();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
        finally { setBusy(false); }
    }

    return (
        <div className="fixed inset-0 z-50 grid place-items-center p-4" style={{ background: "rgba(26,26,26,.45)" }}>
            <div data-testid={`${testid}-dialog`} className="bg-white rounded-[14px] shadow-brand-lg w-full max-w-[640px] max-h-[92vh] flex flex-col border" style={{ borderColor: "#E5E0DA" }}>
                <div className="px-6 pt-5 pb-4 border-b flex items-center justify-between" style={{ borderColor: "#F0EDE8" }}>
                    <h2 className="font-display text-[18px] font-bold text-[#1F1F1F]">{mode === "create" ? "New record" : "Edit record"}</h2>
                    <button data-testid={`${testid}-dialog-close`} onClick={onClose} className="w-9 h-9 rounded-[8px] hover:bg-[#F6F4F0] grid place-items-center"><X className="w-4 h-4" /></button>
                </div>
                <div className="px-6 py-5 flex-1 overflow-y-auto">
                    <div className="grid sm:grid-cols-2 gap-3">
                        {showClientField && (
                            <label className="block sm:col-span-2">
                                <span className="text-[11px] font-semibold mb-1 block" style={{ color: "#6F6A64" }}>Client (leave blank for Priority Risk internal)</span>
                                <select data-testid={`${testid}-field-client_id`} value={form.client_id || ""} onChange={(e) => set("client_id", e.target.value)} className="pr-fi">
                                    <option value="">Priority Risk (internal)</option>
                                    {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                                </select>
                            </label>
                        )}
                        {fields.map((f) => (
                            <label key={f.key} className={`block ${f.half ? "" : "sm:col-span-2"}`}>
                                <span className="text-[11px] font-semibold mb-1 block" style={{ color: "#6F6A64" }}>{f.label}{f.required ? <span className="text-[#B33A2E]"> *</span> : null}</span>
                                {f.type === "select" ? (
                                    <select data-testid={`${testid}-field-${f.key}`} value={form[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} className="pr-fi">
                                        {(f.options || meta[f.optionsKey] || []).map((o) => <option key={o} value={o}>{o}</option>)}
                                    </select>
                                ) : f.type === "textarea" ? (
                                    <textarea data-testid={`${testid}-field-${f.key}`} rows={3} value={form[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} className="pr-fi" />
                                ) : (
                                    <input data-testid={`${testid}-field-${f.key}`} type={f.type === "date" ? "date" : "text"} value={form[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} className="pr-fi" />
                                )}
                            </label>
                        ))}
                    </div>
                    <div className="mt-4 pt-4 border-t" style={{ borderColor: "#F0EDE8" }}>
                        <span className="text-[11px] font-semibold mb-1.5 flex items-center gap-1.5" style={{ color: "#6F6A64" }}><Paperclip className="w-3.5 h-3.5" /> {fileLabel}</span>
                        {record?.attachment && !file && (
                            <div className="text-[12px] mb-1.5" style={{ color: "#2A5DB0" }}>Attached: {record.attachment.filename}</div>
                        )}
                        <label className="inline-flex items-center gap-2 px-3 py-2 rounded-[8px] border cursor-pointer text-[12px] font-medium bg-[#FBFAF8]" style={{ borderColor: "#E5E0DA" }}>
                            <Upload className="w-3.5 h-3.5" style={{ color: "#6F6A64" }} />
                            {file ? file.name : `Choose ${fileLabel.toLowerCase()} file`}
                            <input data-testid={`${testid}-file-input`} type="file" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
                        </label>
                    </div>
                </div>
                <div className="px-6 py-4 border-t flex items-center justify-end gap-3" style={{ borderColor: "#F0EDE8" }}>
                    <button onClick={onClose} disabled={busy} className="px-4 py-2 rounded-[8px] text-[12.5px] font-semibold border bg-white" style={{ borderColor: "#E5E0DA" }}>Cancel</button>
                    <button data-testid={`${testid}-save`} onClick={save} disabled={busy} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-[8px] text-[12.5px] font-semibold text-white" style={{ background: "var(--brand-accent,#C75A1B)" }}>
                        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}{busy ? "Saving…" : "Save"}
                    </button>
                </div>
                <style>{`.pr-fi{width:100%;padding:8px 11px;background:#fff;border:1px solid #E5E0DA;border-radius:8px;font-size:12.5px;color:#1F1F1F;outline:none;}.pr-fi:focus{border-color:#C75A1B;box-shadow:0 0 0 3px #F6E4D8;}`}</style>
            </div>
        </div>
    );
}
